import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  CardMedia,
  Divider,
  InputBase,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { EGIconButton } from "../../MainComponents/Button/IconButton/IconButton";
import { searchNavbarAPI } from "../../../apis";
import { SearchReports } from "./SearchReports/SearchReports";
import { TableSkeleton } from "../../Loading/Table/Table";
import { EGPaginator } from "../../Paginator/Paginator";
import { AdvancedSearch } from "./AdvancedSearch/AdvancedSearch";
import { EGButton } from "../../MainComponents/Button/EGButton/EGButton";
import Etiquin from "../../../assets/images/Ethiquin/ethiquin_no_data.svg";
import { SearchIcon2 } from "../../Icon";

export const GeneralSearchBar = () => {
  // Custom Hooks
  const [t] = useTranslation("report");
  const theme = useTheme();
  const inputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileXS = useMediaQuery(theme.breakpoints.down("sm"));

  // Initial State
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerms, setSearchTerms] = useState(["folio"]);
  const [openAdvanceSearch, setOpenAdvanceSearch] = useState(false);
  // Filters in advanced search
  const [selectFilters, setSelectFilters] = useState({
    status: { value: "", active: false },
    entryway: { value: "", active: false },
    year: { value: "", active: false },
    severity: { value: "", active: false },
  });
  // Table Paginator
  const [reportsData, setReportsData] = useState({});
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [reportLength, setReportLength] = useState(0);

  // Effect to handle the initial fetch (when the component is mounted)
  useEffect(() => {
    if (searchTerms.length > 0) {
      searchingInfo(offset, limit);
    }
  }, [searchTerms, offset, limit]);

  // Effect to clear all filters
  useEffect(() => {
    // Check if all filters are in their initial condition
    const allFiltersCleared = Object.values(selectFilters).every(
      (filter) => !filter.active && filter.value === ""
    );
    // Make request when the filters have been cleared
    if (allFiltersCleared) {
      searchingInfo();
    }
  }, [selectFilters]);

  // Main filters
  const filterOptions = [
    { label: t("searchBar.filter_folio"), value: "folio" },
    { label: t("searchBar.filter_contains"), value: "contains" },
  ];

  // Clean advanced search
  const resetFilters = () => {
    setSelectFilters({
      status: { value: "", active: false },
      entryway: { value: "", active: false },
      year: { value: "", active: false },
      severity: { value: "", active: false },
    });
  };

  // Start on focus input search
  const handleFocus = () => {
    setIsFocused(true);
  };

  // Pressing “ESC” key loses input search focus and closes results
  // Pressing “ENTER” key The search is executed
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        handleClose();
      } else if (event.key === "Enter") {
        event.preventDefault();
        searchingInfo(offset, limit);
      }
    },
    [searchValue, offset, limit]
  );

  // Loses input search focus and closes results
  const handleClose = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    setIsFocused(false);
  };

  // Clean the input without losing focus input search
  const handleClean = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSearchValue("");
    if (isFocused) {
      inputRef.current?.focus();
    }
    searchingInfo(0, 10, "");
  };

  // Function to call the API and fetch data
  const searchingInfo = async (
    newOffset = 0,
    newlimit = 10,
    searchValueParam = searchValue
  ) => {
    setIsLoading(true);

    const data = { offset: newOffset, limit: newlimit };

    searchTerms.forEach((filter) => {
      data[filter] = searchValueParam;
    });

    // Agregar filtros activos de los selects
    Object.keys(selectFilters).forEach((key) => {
      if (selectFilters[key].active && selectFilters[key].value) {
        data[key] = selectFilters[key].value;
      }
    });

    try {
      const resp = await searchNavbarAPI(data);
      setReportsData(resp?.results?.reports || []);
      setCount(Math.ceil(resp.count / newlimit));
      setReportLength(resp.count);
      setOffset(newOffset);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Manejador de cambio de página
  const handlePageChange = (event, newPage) => {
    const newOffset = (newPage - 1) * limit;
    setPage(newPage);
    searchingInfo(newOffset, limit);
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (value === "") {
      searchingInfo(0, limit, value);
      setPage(1);
    }
  };

  // Handles filter selection
  const handleFilterClick = (filterValue) => {
    setSearchTerms((prevFilter) => {
      if (prevFilter.includes(filterValue) && prevFilter.length === 1) {
        return prevFilter;
      }

      const newFilters = prevFilter.includes(filterValue)
        ? prevFilter.filter((filter) => filter !== filterValue)
        : [...prevFilter, filterValue];

      return newFilters;
    });
  };

  return (
    <Box
      ref={searchContainerRef}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        flexFlow: "column nowrap",
        position: isFocused ? "absolute" : "relative",
        top: isFocused ? 12 : "unset",
        left: isFocused ? (isMobile ? -5 : null) : null,
        zIndex: isFocused ? 11 : "auto",
        width: isFocused
          ? isMobile
            ? "93.3vw"
            : "70vw"
          : isMobile
          ? "35vw"
          : "18vw",
        transition: "width 0.3s ease-in-out",
      }}
      component="form"
      onKeyDown={handleKeyDown}
    >
      <AdvancedSearch
        openAdvanceSearch={openAdvanceSearch}
        setOpenAdvanceSearch={setOpenAdvanceSearch}
        selectFilters={selectFilters}
        setSelectFilters={setSelectFilters}
        searchingInfo={searchingInfo}
      />
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          borderRadius: 6,
          height: 40,
          border: `1px solid ${theme.palette.blueGray200.main}`,
          boxShadow: isFocused
            ? `0 0 4px -2px ${alpha(theme.palette.common.black, 0.8)}`
            : `0 0 3px -3px ${alpha(theme.palette.common.black, 0.8)}`,
          overflow: "hidden",
          p: "2px 4px",
          transition: "box-shadow 0.3s ease-in-out",
        }}
      >
        <SearchIcon2
          style={{ marginLeft: 5 }}
          size={18}
          color={theme.palette.blueGray500.main}
          onClick={() => {
            handleFocus();
          }}
        />
        <InputBase
          inputRef={inputRef}
          sx={{ ml: 1, flex: 1 }}
          placeholder={`${t("searchBar.search")}...`}
          inputProps={{ "aria-label": "search casemanager" }}
          onChange={handleSearchChange}
          value={searchValue}
          onFocus={handleFocus}
        />
        {searchValue.length > 0 && (
          <>
            <Divider sx={{ height: 20, m: 0.5 }} orientation="vertical" />
            <EGIconButton
              iconName="close_icon"
              onMouseDown={handleClean}
              iconColorHover="black"
            />
          </>
        )}
        {isFocused && (
          <>
            <Divider sx={{ height: 20, m: 0.5 }} orientation="vertical" />
            <EGIconButton
              iconName="search2"
              iconColorHover="black"
              iconSize="20"
              title={t("searchBar.search")}
              onClick={() => {
                handleFocus();
                searchingInfo(offset, limit);
              }}
            />
            <Divider sx={{ height: 20, m: 0.5 }} orientation="vertical" />
            <EGIconButton
              iconName="settings4"
              sx={{ transform: "rotate(90deg)" }}
              iconColorHover="black"
              title={t("searchBar.advanced_search")}
              placement="bottom"
              onClick={() => setOpenAdvanceSearch((open) => !open)}
            />
          </>
        )}
      </Paper>

      {isFocused && (
        <Paper
          elevation={3}
          sx={{
            mt: 1,
            p: 1,
            minHeight: "90vh",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
          className="dyBorder1 rounded"
        >
          <Box
            sx={{ position: "absolute", top: 2, right: 2 }}
            onClick={handleClose}
          >
            <EGIconButton iconName="cancel_circle" iconColor="primary" />
            <Typography
              variant="body2"
              textAlign="center"
              color="primary"
              mt={-0.7}
              fontSize={8}
              fontWeight={700}
            >
              ESC
            </Typography>
          </Box>

          <Stack
            sx={{
              gap: 1,
              mt: 2,
              mb: 1,
              mr: 3,
              alignItems: "center",
              justifyContent: "space-evenly",
              flexFlow: "row wrap",
            }}
          >
            {filterOptions.map((filter) => (
              <EGButton
                key={filter.value}
                onClick={() => handleFilterClick(filter.value)}
                color={searchTerms.includes(filter.value) ? "primary" : "light"}
                iconName={
                  isMobileXS
                    ? ""
                    : searchTerms.includes(filter.value)
                    ? "check_icon"
                    : "add"
                }
                sx={{
                  py: 0,
                  px: 1,
                  transition: "all 0.5s ease-in-out",
                }}
                label={filter.label}
                iconSize={searchTerms.includes(filter.value) ? 26 : 20}
              />
            ))}
            {Object.values(selectFilters).some((f) => f.active || f.value) && (
              <EGIconButton
                iconName="filter_remove"
                title={t("searchBar.clear_advanced_search")}
                onClick={resetFilters}
                colorHover="error"
                iconColorHover="white"
              />
            )}
          </Stack>

          <Box mt={2}>
            {isLoading ? (
              <TableSkeleton cols={5} rows={10} />
            ) : reportsData.length > 0 ? (
              <Stack>
                <SearchReports
                  data={reportsData}
                  setIsFocused={setIsFocused}
                  isFocused={isFocused}
                  handleClose={handleClose}
                />
                <Stack
                  sx={{
                    mt: 3,
                    px: { md: 3 },
                    justifyContent: { xs: "center", md: "space-between" },
                    flexFlow: "row wrap",
                  }}
                  spacing={1}
                >
                  <Stack
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-around",
                      flexFlow: "row nowrap",
                    }}
                  >
                    <Typography variant="body2" fontSize={11} mr={0.5}>
                      {t("ReportsList.per_page")}:
                    </Typography>
                    <select
                      style={{ border: "none", fontSize: "11px" }}
                      name="limit"
                      value={limit}
                      onChange={(e) => setLimit(e.target.value)}
                    >
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                    </select>
                  </Stack>
                  <EGPaginator
                    onChange={handlePageChange}
                    count={count}
                    page={page}
                    showFirstButton
                    showLastButton
                  />
                  <Typography variant="body2" fontSize={11}>
                    {`${t("ReportsList.results")}: ${
                      count === 0 ? 0 : Number(offset) + 1
                    } ${t("ReportsList.to")} ${
                      reportsData.length + Number(offset)
                    } ${t("ReportsList.of")} ${Number(reportLength)}`}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack
                sx={{
                  height: "50vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CardMedia
                  component="img"
                  image={Etiquin}
                  alt="not_found"
                  sx={{ height: "40%", width: "40%", objectFit: "contain" }}
                />
                <Typography
                  variant="h6"
                  textAlign="center"
                  color="secondary"
                  mt={1}
                >
                  {t("ReportsList.no_results_found")}...
                </Typography>
              </Stack>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
